import type {
  LessonClassStudentsResource,
  TransitionMeetingMyStudentsGroupedResource,
  TransitionMeetingStudentResource,
} from "api/generated";
import type {
  RatingGrade,
  RatingGradeType,
  TeacherMeetingGrouped,
  TeacherStudentRating,
  TeacherSubjectGroup,
} from "meetings/advisory/types";
import { toAbsoluteImageUrl } from "shared/utils";
import { answerOptionFromDto } from "./answerOption.mapper";
import { lessonClassGroupFromDto } from "./meetingMyStudents.mapper";

let nextTempId = -1;

const gradeFromDto = (dto: { type: string; grade: string }): RatingGrade => ({
  type: dto.type as RatingGradeType,
  grade: dto.grade,
});

const studentRatingFromDto = (
  dto: TransitionMeetingStudentResource,
): TeacherStudentRating => ({
  id: dto.rating?.id ?? nextTempId--,
  studentId: dto.id,
  student: {
    id: dto.id,
    formattedName: dto.formatted_name,
    profileImage: toAbsoluteImageUrl(dto.profile_image),
  },
  rating: dto.rating?.rating ?? null,
  feedback: dto.rating?.feedback ?? null,
  hasHistory: dto.has_history,
  teacherShortage: dto.rating?.teacher_shortage ?? undefined,
  grades: (dto.rating?.grades ?? []).map(gradeFromDto),
});

const lessonClassFromDto = (
  dto: LessonClassStudentsResource,
): TeacherSubjectGroup => ({
  lessonClass: dto.lesson_class,
  subject: dto.subject,
  students: dto.students.map(studentRatingFromDto),
});

export const teacherMeetingGroupedFromDto = (
  dto: TransitionMeetingMyStudentsGroupedResource,
): TeacherMeetingGrouped => ({
  meeting: {
    name: dto.meeting.name,
    status: dto.meeting.status as TeacherMeetingGrouped["meeting"]["status"],
    message: dto.meeting.message ?? null,
    progressPercentage:
      dto.meeting.total_ratings > 0
        ? Math.round(
            (dto.meeting.total_given_ratings / dto.meeting.total_ratings) * 100,
          )
        : 0,
    opensAt: dto.meeting.opens_at ? new Date(dto.meeting.opens_at) : null,
    closedModifyAt: dto.meeting.closed_modify_at
      ? new Date(dto.meeting.closed_modify_at)
      : null,
    closedAt: dto.meeting.closed_at ? new Date(dto.meeting.closed_at) : null,
    ratingOptions: dto.rating_options.map(answerOptionFromDto),
    questionPrompt: dto.question_prompt,
    gradeSettings: {
      enableSeGrade: dto.grade_settings.enable_se_grade,
      enableRapportGrade: dto.grade_settings.enable_rapport_grade,
    },
  },
  groups: dto.groups.map(lessonClassGroupFromDto),
  lessonClasses: dto.lesson_classes.map(lessonClassFromDto),
  activeClass: dto.activeClass,
  meta: {
    hasMore: dto.meta.has_more,
    offset: dto.meta.offset,
    take: dto.meta.take,
    total: dto.meta.total,
  },
});
